// # itemsFromJob
// {
//     "uniqueid": "35F7C94101",
//         "subitemid": 57977,
//             "name": "Item B (printer1)",
//                 "price": 10,
//                     "discountrequest": {
//         "mode": 0,
//             "value": 0
//     },
//     "quantity": 4,
//         "toppings": [],
//             "taglist": [
//                 "printer1",
//                 "no-discount"
//             ],
//                 "majorset": 2,
//                     "key": "57977:10:0:0",
//                         "discountRequest": {
//         "mode": 0,
//             "value": 0
//     },
//     "addFrom": "Move to",
//         "refs": {
//         "35F7C94101": true
//     }
// }

import type { TableItem } from "~/interface/TableItem"
import type { TableBilling } from "~/interface/TableBilling"
import type { Table } from "~/interface/Table";
import type { TableSetting } from "~/interface/TableState";
import dayjs from "dayjs";
import type { JobCard } from "~/interface/Job";
import { roundMoney } from "~/utils/money";
import { getActiveBillingStrategy } from "./billing";

// Imported for local use in calculateItems AND re-exported for existing importers
// (e.g. pos.cartItems.ts). The single source of truth now lives in the billing
// module so strategies can share it.
// NOTE: this must be a real import + `export {}`, not `export ... from`. A bare
// re-export creates no local binding, so calculateItems' call below would throw
// ReferenceError on every billing pass.
import { discountLineFn, discountItemLineFn } from "./billing/discountLine";
export { discountLineFn, discountItemLineFn };


interface ToppingItem {
    toppingId?: string | number;
    name?: string;
    price?: number;
    discount?: number;
    quantity?: number;
}

export const calculateItems = (
    itemsFromJob: any[],
    noDiscountResolver?: (subItemId: any) => boolean,
    skipServiceResolver?: (subItemId: any) => boolean,
): TableItem[] => {
    if (!itemsFromJob || !Array.isArray(itemsFromJob)) {
        return [];
    }

    return itemsFromJob.map((item: any) => {
        const price = item.price || 0;
        const quantity = item.quantity || 0;

         
        const _toppings = (item.toppings || []).map((topping: ToppingItem) => {
            const toppingPrice = topping.price || 0;
            const toppingQuantity = topping.quantity || topping.qty || 0;
            const toppingDiscount = topping.discount || 0;
            const toppingExtendedPrice = roundMoney(toppingPrice * toppingQuantity);
            const toppingDiscountedPrice = roundMoney(toppingExtendedPrice - toppingDiscount);

            return {
                toppingId: topping.toppingId?.toString() || '',
                name: topping.name || '',
                price: toppingPrice,
                discount: toppingDiscount,
                discountedPrice: toppingDiscountedPrice,
                quantity: toppingQuantity,
                extendedPrice: toppingExtendedPrice,
            };
        })

        // Rounded because it is stored on the receipt as `sumToppings` AND feeds
        // extendedPrice — both must agree at 2 decimals.
        const _toppingSum = roundMoney(_toppings.reduce((acc: number, topping: typeof _toppings[0]) => {
            return acc + (topping.price * topping.quantity);
        }, 0));

        // Every per-line money value is rounded to 2 decimals here so that the
        // bill-level sums (SubTotal = Σ extendedPrice, ItemDiscount = Σ discount)
        // are exact — a sum of 2-decimal values needs no further repair.
        const extendedPrice = roundMoney((price + _toppingSum) * quantity)

        // A mode-1 (baht) item discount is PER PIECE — `value` is the per-unit
        // discount and the ×quantity happens here, so void/split/move re-price
        // on their own. See billing/discountLine.ts.
        const _discountAmount = roundMoney(-1 * discountItemLineFn(
            extendedPrice,
            quantity,
            item.discountRequest?.mode || 0,
            item.discountRequest?.value || 0));

        const discountedPrice = roundMoney(extendedPrice + _discountAmount)

        return {
            uuid: item.uniqueid || '',
            subItemId: item.subitemid?.toString() || '',
            name: item.name || '',
            comments: item.comments || '',
            price: price,
            discountedPrice: discountedPrice,
            dc1: [
                item.discountRequest?.mode || 0,
                item.discountRequest?.value || 0,
                _discountAmount
            ],
            quantity: quantity,
            extendedPrice: extendedPrice,
            toppings: _toppings,
            toppingsAmount: _toppingSum,
            bill: item.bill || 1,
            isOpenMenu:  item.isOpenMenu || false,
            noDiscount: noDiscountResolver ? !!noDiscountResolver(item.subitemid) : false,
            // Divana ("Product" catalog Type) → VAT-inclusive & service-exempt.
            // Resolved from the sub-item id at build time (same pattern as noDiscount);
            // honored only by strategies that read it (see DivanaBillingStrategy).
            skipService: skipServiceResolver ? !!skipServiceResolver(item.subitemid) : false,
            ...(item.openMenuName ? { openMenuName: item.openMenuName } : {}),
        } as TableItem;
    });
}



// Dispatches to the shop's billing strategy, resolved once at startup from the
// `BillingStrategy` setting (see billing/index.ts + settings.computeInit). The
// default strategy is the standard pipeline and is what every shop gets unless it
// opts in. Call sites are unchanged.
export const calculateBilling = (items: TableItem[], globalOptions: any, tableOptions: any, opts?: { noServiceCharge?: boolean }): TableBilling => {
    return getActiveBillingStrategy().calculateBilling(items, globalOptions, tableOptions, opts)
}


export const createTableInstance = (table: Table, tableBillingGlobal : TableSetting, tableSetting : TableSetting, opts?: { noServiceCharge?: boolean, noDiscountResolver?: (subItemId: any) => boolean, skipServiceResolver?: (subItemId: any) => boolean }): Table => {



    const items = calculateItems(table.__items, opts?.noDiscountResolver, opts?.skipServiceResolver)
    const  billing = calculateBilling(items, tableBillingGlobal, tableSetting, opts)

    const tableInstance: Table = {
        uuid: table.uuid,
        name: table.name,
        nickName: table.nickName || '',
        guests: (tableSetting?.guests ?? table.guests) || 2,
        // Fallback must be DETERMINISTIC per recompute: this field feeds the
        // publish fingerprints (cloud.tables-state / mobile-v1.sync), and a
        // fresh dayjs() here made them differ every second — republishing an
        // unchanged table to RTDB on every recompute. startTime is stamped once
        // from the first job (JobchainV1-1:81) and carried over on replay.
        startDateTime: table.startDateTime
            || (table.startTime ? dayjs(table.startTime).format('YYYY-MM-DD HH:mm:ss') : ''),
        employeeName: table.employeeName || '',
        // __items: [],
        items: items,
        jobs: table.jobs || [],
        mode: table.mode || 1,
        billing: billing,
        setting: tableSetting || {},
        deliveryInfo: table.deliveryInfo || null,
        lastUpdate: table.lastUpdate || ''
    }

    return tableInstance;
} 
